import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScanSearch, Loader2 } from "lucide-react";

const AUTH_BG =
  "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG1pbmltYWwlMjBnZW9tZXRyaWMlMjBiYWNrZ3JvdW5kJTIwc3RhcmslMjBjb250cmFzdHxlbnwwfHx8fDE3ODMyMzI0NjF8MA&ixlib=rb-4.1.0&q=85";

export function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-white/15 backdrop-blur flex items-center justify-center">
              <ScanSearch className="w-5 h-5" />
            </div>
            <span className="font-heading font-black text-2xl tracking-tighter">Sapientia</span>
          </div>
          <div>
            <h2 className="font-heading text-4xl font-black tracking-tighter leading-tight">
              Analyze any website.<br />Fix your SEO in minutes.
            </h2>
            <p className="mt-4 text-white/70 max-w-md">
              Instant on-page, technical & keyword audits with AI-powered recommendations you can act on.
            </p>
          </div>
          <div className="uppercase-label text-white/40">Built for growth teams</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md bg-klein flex items-center justify-center">
              <ScanSearch className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-black text-xl tracking-tighter">Sapientia</span>
          </div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">{title}</h1>
          <p className="text-gray-500 mt-2 mb-8">{subtitle}</p>
          {children}
          <div className="mt-6 text-sm text-gray-500 text-center">{footer}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to run audits and view your saved reports."
      footer={
        <>
          No account?{" "}
          <Link to="/register" data-testid="go-to-register" className="text-klein font-semibold hover:underline">
            Create one
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="login-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1.5"
            placeholder="you@company.com"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" data-testid="go-to-forgot" className="text-xs text-klein font-semibold hover:underline">
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            data-testid="login-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1.5"
            placeholder="••••••••"
          />
        </div>
        {error && (
          <p data-testid="login-error" className="text-sm text-bad font-medium">
            {error}
          </p>
        )}
        <Button
          type="submit"
          data-testid="login-submit"
          disabled={loading}
          className="w-full bg-[#0A0A0A] hover:bg-gray-800 py-6 text-base"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
